import { useState, useEffect, useRef } from "react";
import { format, parse, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { CalendarDays } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

const ISO = "yyyy-MM-dd";
const FR = "dd/MM/yyyy";

const isoToFr = (iso) => {
  if (!iso) return "";
  const d = parse(String(iso).slice(0, 10), ISO, new Date());
  return isValid(d) ? format(d, FR) : "";
};

const frToIso = (text) => {
  const d = parse(text, FR, new Date());
  return isValid(d) ? format(d, ISO) : null;
};

// Ajoute les « / » au fil de la saisie : 21062026 -> 21/06/2026
const autoSlash = (raw) => {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/**
 * Champ de date au format français jj/mm/aaaa, indépendant de la locale du navigateur.
 * Compatible en remplacement direct de <DateField> : `onChange` reçoit
 * un événement synthétique `{ target: { value: "yyyy-MM-dd" } }`.
 */
export function DateField({
  value,
  onChange,
  className,
  disabled,
  id,
  placeholder = "jj/mm/aaaa",
  "data-testid": testid,
}) {
  const [text, setText] = useState(() => isoToFr(value));
  const [open, setOpen] = useState(false);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setText(isoToFr(value));
  }, [value]);

  const emit = (iso) => onChange?.({ target: { value: iso } });

  const handleType = (e) => {
    const next = autoSlash(e.target.value);
    setText(next);
    if (next === "") { emit(""); return; }
    const iso = frToIso(next);
    if (iso) emit(iso);
  };

  const selected = (() => {
    if (!value) return undefined;
    const d = parse(String(value).slice(0, 10), ISO, new Date());
    return isValid(d) ? d : undefined;
  })();

  return (
    <div className={cn("relative", className)}>
      <input
        id={id}
        type="text"
        inputMode="numeric"        disabled={disabled}
        value={text}
        placeholder={placeholder}
        onFocus={() => { focused.current = true; }}
        onBlur={() => { focused.current = false; setText(isoToFr(value)); }}
        onChange={handleType}
        data-testid={testid}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 pr-8 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
      />
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Ouvrir le calendrier"
            data-testid={testid ? `${testid}-calendar-btn` : undefined}
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-indigo-600 disabled:opacity-50"
          >
            <CalendarDays size={15} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            locale={fr}
            weekStartsOn={1}
            selected={selected}
            defaultMonth={selected}
            onSelect={(d) => {
              if (d) { emit(format(d, ISO)); setText(format(d, FR)); }
              setOpen(false);
            }}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export default DateField;
